import { ActivityService } from "./ActivityService";
import { ArticleService } from "./ArticleService";
import { CertificateService } from "./CertificateService";
import { ICertificateServiceEndpoint } from "../models/Certificate/ICertificateServiceEndpoint"
import { ComponentFactory } from "./ComponentFactory";
import { EmailSignUpService } from "./EmailSignUpService";
import { GamificationService } from "./GamificationService";
import { HealioStoreService } from "./HealioStoreService";
import { HssPollAnswerService } from "./HssPollAnswerService";
import { IActivityServiceEndpoint } from "../models/IActivityServiceEndpoint";
import { IArticleServiceEndpoint } from "../models/IArticleServiceEndpoint";
import { IGamificationServiceEndpoint } from "../models/Gamification/IGamificationServiceEndpoint";
import { IHealioStoreEndpoint } from "../models/HealioBigComm/IHealioStoreEndpoint";
import { IEndpoint } from "../models/IEndpoint";
import { ITranscriptEndpoint } from "../models/Transcript/ITranscriptEndpoint";
import { LandingPageService } from "./LandingPageService";
import { MyCmeService } from "./MyCmeService";
import { NotificationBoxService } from "./NotificationBoxService"
import { QBankService } from "./QBankService";
import { QuestionAnswerService } from "./QuestionAnswerService";
import { RecommendedService } from "./RecommendedService";
import { SavedPageService } from "./SavedPageService";
import { TopicAlertService } from "./TopicAlertService";
import { TopicListService } from "./TopicListService";
import { TranscriptService } from "./TranscriptService";
import { UserActivityService } from "./UserActivityService";
import { UserProfileService } from "./UserProfileService";
import { QuikQuizService } from "./QuikQuizService";
import { FindRequirementsService } from "./FindRequirementsService";
import { UserGoalsService } from "./UserGoalsService"
import { UserLoginService } from "./UserLoginService"
import { CombinedActivityService } from "./CombinedActivityService";

import { IUserLoginServiceEndpoint } from "../models/UserProfile/IUserLoginServiceEndpoint"
import { IUserGoalsServiceEndpoint } from "../models/UserGoals/IUserGoalsServiceEndpoint"
import { IFindRequirementsServiceEndpoint } from "../models/CMERequirements/IFindRequirementsServiceEndpoint";
import { IPollAnswerServiceEndpoint } from "../models/HssPoll/IPollAnswerServiceEndpoint";
import { IQBankServiceEndpoint } from "../models/QBank/IQBankServiceEndpoint";
import { IQuestionAnswerServiceEndpoint } from "../models/QuestionGroup/IQuestionAnswerServiceEndpoint";
import { ITopicAlertServiceEndpoint } from "../models/TopicAlert/ITopicAlertServiceEndpoint";
import { ITopicListServiceEndpoint } from "../models/TopicList/ITopicListServiceEndpoint";
import { IUserActivityServiceEndpoint } from "../models/UserActivity/IUserActivityServiceEndpoint";
import { IQuikQuizServiceEndpoint } from "../models/QuikQuiz/IQuikQuizServiceEndpoint"
import { IUserProfileServiceEndpoint } from "../models/UserProfile/IUserProfileServiceEndpoint";
import { INotificationBoxServiceEndpoint } from "../models/NotificationBox/INotificationBoxServiceEndpoint";
import { IMyHealioCmeEndpoint } from "../models/MyCme/IMyHealioCmeEndpoint";

export class PageContext {

  public readonly componentFactory: ComponentFactory =
    new ComponentFactory(this);

  private readonly _data: DOMStringMap | null;

  private _userLoginService: UserLoginService | null = null;
  private _userGoalsService: UserGoalsService | null = null;
  private _findRequirementsService: FindRequirementsService | null = null;
  private _activityService: ActivityService | null = null;
  private _articleService: ArticleService | null = null;
  private _certificateService: CertificateService | null = null;
  private _emailSignUpService: EmailSignUpService | null = null;
  private _gamificationService: GamificationService | null = null;
  private _healioStoreService: HealioStoreService | null = null;
  private _hssPollAnswerService: HssPollAnswerService | null = null;
  private _myCmeService: MyCmeService | null = null;
  private _notificationBoxService: NotificationBoxService | null = null;
  private _qbankService: QBankService | null = null;
  private _questionAnswerService: QuestionAnswerService | null = null;
  private _quikQuizService: QuikQuizService | null = null;
  private _recommendedService: RecommendedService | null = null;
  private _savedPageService: SavedPageService | null = null;
  private _topicAlertService: TopicAlertService | null = null;
  private _topicListService: TopicListService | null = null;
  private _transcriptService: TranscriptService | null = null;
  private _userActivityService: UserActivityService | null = null;
  private _userProfileService: UserProfileService | null = null;
  private _combinedActivityService: CombinedActivityService | null = null;

  constructor(contextElement?: HTMLElement) {
    const element =
      contextElement ||
      document.querySelector("[data-wyanoke-page-context]") as HTMLElement;

    if (!element) {
      console.warn("Could not locate context element.");
    }

    this._data = element ? element.dataset : null;
  }

  private get userLoginServiceEndpoint(): IUserLoginServiceEndpoint {
    const data = this._data;
    const get = data ? data.userLogin || null : null;

    return {
      get: PageContext.parseEndpoint(get)
    }
  }

  private get userGoalsServiceEndpoint(): IUserGoalsServiceEndpoint {
    const data = this._data;
    const update = data? data.eduUserGoalsUpdate || null : null;

    return {
      update: PageContext.parseEndpoint(update)
    };
  }

  private get findRequirementsServiceEndpoint(): IFindRequirementsServiceEndpoint {
    const data = this._data;
    const load = data? data.findRequirementsLoad || null : null;

    return {
      load: PageContext.parseEndpoint(load)
    };
  }

  private get activityServiceEndpoint(): IActivityServiceEndpoint {
    const data = this._data;
    const load = data ? data.activityServiceLoad || null : null;

    return {
      load: PageContext.parseEndpoint(load)
    };
  }

  private get articleServiceEndpoint(): IArticleServiceEndpoint {
    const data = this._data;
    const load = data ? data.articleServiceLoad || null : null;
    const schemaDotOrg = data ? data.articleServiceSchemaDotOrg || null : null;

    return {
      load: PageContext.parseEndpoint(load),
      schemaDotOrg: PageContext.parseEndpoint(schemaDotOrg)
    };
  }

  private get emailSignUpEndpoint(): IEndpoint | null {
    const data = this._data;
    const save = data ? data.emailSignUpSave || null : null;

    return PageContext.parseEndpoint(save);
  }

  private get healioStoreServiceEndpoint(): IHealioStoreEndpoint | null {
    const data = this._data;
    const addProductToCart = data ? data.healioStoreAddProductToCart || null : null;
    const productsByCategoryId = data ? data.healioStoreProductsByCategoryId || null : null;

    return {
      addProductToCart: PageContext.parseEndpoint(addProductToCart),
      productsByCategoryId: PageContext.parseEndpoint(productsByCategoryId),
    };
  }

  private get hssPollAnswerEndpoint(): IPollAnswerServiceEndpoint | null {
    const data = this._data;
    const getAnswerCounts = data ? data.hssGetAnswerCounts || null : null;
    const load = data ? data.hssPollAnswersLoad || null : null;
    const save = data ? data.hssPollAnswersSave || null : null;

    return {
      getAnswerCounts: PageContext.parseEndpoint(getAnswerCounts),
      load: PageContext.parseEndpoint(load),
      save: PageContext.parseEndpoint(save)
    };
  }

  private get qbankServiceEndpoint(): IQBankServiceEndpoint | null {
    const data = this._data;
    const saveAnswer = data ? data.qbankSaveAnswer || null : null;
    const completeQuiz = data ? data.qbankCompleteQuiz || null : null;

    return {
      completeQuiz: PageContext.parseEndpoint(completeQuiz),
      saveAnswer: PageContext.parseEndpoint(saveAnswer)
    };
  }

  private get quikQuizServiceEndpoint(): IQuikQuizServiceEndpoint | null {
    const data = this._data;
    const getCurrentTake = data ? data.quikQuizGetCurrentTake || null : null;

    return {
      getCurrentTake: PageContext.parseEndpoint(getCurrentTake)
    }
  }

  private get myCmeEndpoint(): IMyHealioCmeEndpoint | null {
    const data = this._data;
    const load = data ? data.myCmeLoad || null : null;
    const jsonLoad = data ? data.myCmeJsonLoad || null : null;

    return {
      load: PageContext.parseEndpoint(load),
      jsonLoad: PageContext.parseEndpoint(jsonLoad)
    }
  }

  private get notificationBoxEndpoint(): INotificationBoxServiceEndpoint | null {
    const data = this._data;
    const load = data ? data.eduNotificationBoxNotifications || null : null;
    const deleteNotification = data ? data.eduNotificationBoxDelete || null : null;
    const updateSettings = data ? data.eduNotificationBoxUpdateSettings || null : null;

    return {
      load: PageContext.parseEndpoint(load),
      deleteNotification: PageContext.parseEndpoint(deleteNotification),
      updateSettings: PageContext.parseEndpoint(updateSettings)
    };
  }

  private get questionAnswerEndpoint(): IQuestionAnswerServiceEndpoint | null {
    const data = this._data;
    const load = data ? data.questionAnswerLoad || null : null;
    const save = data ? data.questionAnswerSave || null : null;
    const compareToPeers = data ? data.questionAnswerCompareToPeers || null : null;
    return {
      load: PageContext.parseEndpoint(load),
      save: PageContext.parseEndpoint(save),
      compareToPeers: PageContext.parseEndpoint(compareToPeers)
    };
  }

  private get recommendedEndpoint(): IEndpoint | null {
    const data = this._data;
    const load = data ? data.recommendedServiceLoad || null : null;

    return PageContext.parseEndpoint(load);
  }

  private get topicAlertEndpoint(): ITopicAlertServiceEndpoint | null {
    const data = this._data;
    const load = data ? data.topicAlertLoad || null : null;
    const register = data ? data.topicAlertRegister || null : null;
    const save = data ? data.topicAlertSave || null : null;

    return {
      load: PageContext.parseEndpoint(load),
      register: PageContext.parseEndpoint(register),
      save: PageContext.parseEndpoint(save)
    };
  }

  private get topicListApi(): ITopicListServiceEndpoint | null {
    const data = this._data;
    const topics = data ? data.topicListApi || null : null;
    const topicsAndCreditTypes = data ? data.topicAndCreditTypeListApi || null : null;

    return {
      topics: PageContext.parseEndpoint(topics),
      topicsAndCreditTypes: PageContext.parseEndpoint(topicsAndCreditTypes),
    }
  }

  private get transcriptServiceEndpoint(): ITranscriptEndpoint | null {
    const data = this._data;
    const generate = data ? data.generateTranscript || null : null;
    const email = data ? data.emailTranscript || null : null;

    return {
      email: PageContext.parseEndpoint(email),
      generate: PageContext.parseEndpoint(generate)
    };
  }

  private get userActivityEndpoint(): IUserActivityServiceEndpoint | null {
    const data = this._data;
    const filter = data ? data.userActivityFilter || null : null;
    const load = data ? data.userActivityLoad || null : null;
    const remove = data ? data.userActivityRemove || null : null;
    const save = data ? data.userActivitySave || null : null;
    const generateCertificate = data ? data.userActivityGenerateCertificate ||
      null : null;
    const updateCertificate = data ? data.userActivityUpdateCertificate ||
      null : null;
    const updateStep = data ? data.userActivityUpdateStep || null : null;
    const removeNotification = data ? data.userActivityNotificationRemove || null : null;

    return {
      filter: PageContext.parseEndpoint(filter),
      generatecertificate: PageContext.parseEndpoint(generateCertificate),
      load: PageContext.parseEndpoint(load),
      remove: PageContext.parseEndpoint(remove),
      save: PageContext.parseEndpoint(save),
      updatecertificate: PageContext.parseEndpoint(updateCertificate),
      updatestep: PageContext.parseEndpoint(updateStep),
      removenotification: PageContext.parseEndpoint(removeNotification)
    };
  }

  private get gamificationEndpoint(): IGamificationServiceEndpoint | null {
    const data = this._data;
    const load = data ? data.gamificationLoad || null : null;
    const save = data ? data.gamificationSave || null : null;
    const loadall = data ? data.gamificationLoadAll || null : null;
    const getrelatedcontent = data ? data.gamificationGetRelatedContent || null : null;

    return {
      getrelatedcontent: PageContext.parseEndpoint(getrelatedcontent),
      load: PageContext.parseEndpoint(load),
      loadall: PageContext.parseEndpoint(loadall),
      save: PageContext.parseEndpoint(save)
    };
  }

  private get certificateEndpoint(): ICertificateServiceEndpoint | null {
    const data = this._data;
    const zip = data ? data.certificateZip || null : null;
    const path = data ? data.certificatePath || null : null;
    const email = data ? data.certificateEmail || null : null;
    const emailzip = data ? data.certificateEmailZip || null : null;

    return {
      zip: PageContext.parseEndpoint(zip),
      path: PageContext.parseEndpoint(path),
      email: PageContext.parseEndpoint(email),
      emailzip: PageContext.parseEndpoint(emailzip)
    };
  }

  private get userProfileEndpoint(): IUserProfileServiceEndpoint | null {
    const data = this._data;
    const update = data ? data.userProfileUpdate || null : null;
    const getCustomFields = data ? data.userProfileGetCustomFields || null : null;
    const updateUserSiteSettings = data ? data.userProfileUpdateSiteSettings || null : null;

    return {
      getCustomFields: PageContext.parseEndpoint(getCustomFields),
      update: PageContext.parseEndpoint(update),
      updateSiteSettings: PageContext.parseEndpoint(updateUserSiteSettings)
    };
  }

  private static parseEndpoint(raw?: string | null): IEndpoint | null {
    const parts = raw ? raw.split(":") : null;
    if (!parts) {
      return null;
    }

    const method = parts[0].toUpperCase() === "POST" ? "POST" :
      parts[0].toUpperCase() === "DELETE" ? "DELETE" :
        parts[0].toUpperCase() === "PUT" ? "PUT" : "GET";

    return parts ? {
      method,
      url: parts[1]
    } : null;
  }

  // see nyc (not-you-chris)
  // tslint:disable-next-line: no-any
  public getCardService(serviceName: string | null): any {
    if (serviceName === null || serviceName === "") {
      return null;
    }

    const data = this._data;
    const load = data ? data[serviceName] || null : null;

    return new LandingPageService({
      load: PageContext.parseEndpoint(load)
    });
  }

  public get activityService(): ActivityService | null {
    if (this._activityService === null) {
      const endpoint = this.activityServiceEndpoint;
      this._activityService = new ActivityService(endpoint);
    }

    return this._activityService;
  }

  public get findRequirementsService(): FindRequirementsService | null {
    if (this._findRequirementsService === null) {
      const endpoint = this.findRequirementsServiceEndpoint;
      this._findRequirementsService = new FindRequirementsService(endpoint);
    }

    return this._findRequirementsService;
  }

  public get userLoginService(): UserLoginService | null {
    if(this._userLoginService === null) {
      const endpoint = this.userLoginServiceEndpoint;
      this._userLoginService = new UserLoginService(endpoint);
    }

    return this._userLoginService;
  }

  public get userGoalsService(): UserGoalsService | null {
    if (this._userGoalsService === null) {
      const endpoint = this.userGoalsServiceEndpoint;
      this._userGoalsService = new UserGoalsService(endpoint);
    }

    return this._userGoalsService;
  }

  public get articleService(): ArticleService | null {
    if (this._activityService === null) {
      const endpoint = this.articleServiceEndpoint;
      this._articleService = new ArticleService(endpoint);
    }

    return this._articleService;
  }

  public get emailSignUpService(): EmailSignUpService | null {
    if (this._emailSignUpService === null) {
      const endpoint = this.emailSignUpEndpoint;
      if (endpoint !== null) {
        this._emailSignUpService = new EmailSignUpService(endpoint);
      }
    }

    return this._emailSignUpService;
  }

  public get healioStoreService(): HealioStoreService | null {
    if (this._healioStoreService === null) {
      const endpoint = this.healioStoreServiceEndpoint;
      if (endpoint !== null) {
        this._healioStoreService = new HealioStoreService(endpoint);
      }
    }

    return this._healioStoreService;
  }

  public get hssPollAnswerService(): HssPollAnswerService | null {
    if (this._hssPollAnswerService === null) {
      const endpoint = this.hssPollAnswerEndpoint;
      if (endpoint !== null) {
        this._hssPollAnswerService = new HssPollAnswerService(endpoint);
      }
    }

    return this._hssPollAnswerService;
  }

  public get qbankService(): QBankService | null {
    if (this._qbankService === null) {
      const endpoint = this.qbankServiceEndpoint;
      if (endpoint !== null) {
        this._qbankService = new QBankService(endpoint);
      }
    }

    return this._qbankService;
  }

  public get quikQuizService(): QuikQuizService | null {
    if (this._quikQuizService === null) {
      const endpoint = this.quikQuizServiceEndpoint;
      if (endpoint !== null) {
        this._quikQuizService = new QuikQuizService(endpoint);
      }
    }

    return this._quikQuizService;
  }

  public get myCmeService(): MyCmeService | null {
    if (this._myCmeService === null) {
      const endpoint = this.myCmeEndpoint;
      if (endpoint !== null) {
        this._myCmeService = new MyCmeService(endpoint);
      }
    }

    return this._myCmeService;
  }

  public get notificationBoxService(): NotificationBoxService | null {
    if (this._notificationBoxService === null) {
      const endpoint = this.notificationBoxEndpoint;
      if (endpoint !== null) {
        this._notificationBoxService = new NotificationBoxService(endpoint);
      }
    }

    return this._notificationBoxService;
  }

  public get questionAnswerService(): QuestionAnswerService | null {
    if (this._questionAnswerService === null) {
      const endpoint = this.questionAnswerEndpoint;
      if (endpoint !== null) {
        this._questionAnswerService = new QuestionAnswerService(endpoint);
      }
    }

    return this._questionAnswerService;
  }

  public get recommendedService(): RecommendedService | null {
    if (this._recommendedService === null) {
      const endpoint = this.recommendedEndpoint;
      if (endpoint !== null) {
        this._recommendedService = new RecommendedService(endpoint);
      }
    }

    return this._recommendedService;
  }

  public get savedPageService(): SavedPageService | null {
    if (this._savedPageService === null) {
      this._savedPageService = new SavedPageService();
    }

    return this._savedPageService;
  }

  public get topicAlertService(): TopicAlertService | null {
    if (this._topicAlertService === null) {
      const endpoint = this.topicAlertEndpoint;
      if (endpoint !== null) {
        this._topicAlertService = new TopicAlertService(endpoint);
      }
    }

    return this._topicAlertService;
  }

  public get topicListService(): TopicListService | null {
    if (this._topicListService === null) {
      const endpoint = this.topicListApi;
      if (endpoint !== null) {
        this._topicListService = new TopicListService(endpoint);
      }
    }

    return this._topicListService;
  }

  public get transcriptService(): TranscriptService | null {
    if (this._transcriptService === null) {
      const endpoint = this.transcriptServiceEndpoint;
      if (endpoint) {
        this._transcriptService = new TranscriptService(endpoint);
      }
    }

    return this._transcriptService;
  }

  public get certificateService(): CertificateService | null {
    if (this._certificateService === null) {
      /* const data = this._data;
      const raw = data ? data.certificateEmail : null; */
      const endpoint = this.certificateEndpoint;
      if (!endpoint) {
        return null;
      }

      this._certificateService = new CertificateService(endpoint);
    }

    return this._certificateService;
  }

  public get userActivityService(): UserActivityService | null {
    if (this._userActivityService === null) {
      const endpoint = this.userActivityEndpoint;
      if (endpoint !== null) {
        this._userActivityService = new UserActivityService(endpoint);
      }
    }

    return this._userActivityService;
  }

  public get gamificationService(): GamificationService | null {
    if (this._gamificationService === null) {
      const endpoint = this.gamificationEndpoint;
      if (endpoint !== null) {
        this._gamificationService = new GamificationService(endpoint);
      }
    }

    return this._gamificationService;
  }

  public get userProfileService(): UserProfileService | null {
    if (this._userProfileService === null) {
      const endpoint = this.userProfileEndpoint;
      if (endpoint !== null) {
        this._userProfileService = new UserProfileService(endpoint);
      }
    }

    return this._userProfileService;
  }

  public get combinedActivityService(): CombinedActivityService | null {
    if (this._combinedActivityService === null) {
      const endpoint = this.userActivityEndpoint;
      if (endpoint !== null) {
        this._combinedActivityService = new CombinedActivityService(endpoint);
      }
    }

    return this._combinedActivityService;
  }
}
